import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserPlus, Lock, Unlock, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormDialog, Loading, Pill, Table, Title, confirmRun, fmtDT, useData } from "@/ops/ui";

const FIELDS = [
  { k: "name", label: "İşletme adı", required: true, wide: true }, { k: "tax_no", label: "Vergi no" },
  { k: "contact_name", label: "Yetkili kişi" }, { k: "phone", label: "Telefon", type: "tel" },
  { k: "email", label: "E-posta", type: "email", empty: null }, { k: "address", label: "Adres", type: "textarea" },
  { k: "active", label: "Aktif müşteri (kapatılırsa tüm müşteri kullanıcılarının erişimi kesilir)", type: "bool" },
];
const USER_FIELDS = [{ k: "name", label: "Ad Soyad", required: true }, { k: "email", label: "E-posta (giriş için)", type: "email", required: true },
  { k: "password", label: "Geçici şifre (en az 10 karakter)", required: true, wide: true, hint: "Kullanıcı ilk girişte şifresini değiştirmek zorundadır." }];
const PW = [{ k: "password", label: "Yeni geçici şifre", required: true, wide: true }];

function userStatus(u) {
  if (!u.active) return "İptal edildi";
  return u.must_change ? "Şifre bekliyor" : "Açık";
}

const USER_HEAD = ["Ad", "E-posta", "Durum", "Son giriş", ""];
const CLIENT_HEAD = ["Kod", "İşletme", "Yetkili", "Telefon", "Proje", "Durum", ""];
const NO_INITIAL = {};

function UsersDialog({ client, onClose }) {
  const [users, reload] = useData(client ? `/ops/admin/clients/${client.id}/users` : null);
  const [add, setAdd] = useState(false);
  const [pw, setPw] = useState(null);
  const toggle = (u) => confirmRun(u.active ? "Bu kullanıcının erişimi iptal edilsin mi?" : "Erişim yeniden açılsın mı?", async () => { await api.patch(`/ops/admin/users/${u.id}`, { active: !u.active }); reload(); }, "Erişim güncellendi");
  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="client-users-dialog">
        <DialogHeader><DialogTitle>{client?.name} – Müşteri Kullanıcıları</DialogTitle></DialogHeader>
        <button onClick={() => setAdd(true)} className="btn-red w-fit !py-2" data-testid="client-user-add-btn"><UserPlus className="h-4 w-4" /> Kullanıcı Ekle</button>
        {!users ? <Loading /> : (
          <Table head={USER_HEAD} testid="client-users-table">
            {users.map((u) => (
              <tr key={u.id}><td className="px-3 py-2 font-semibold">{u.name}</td><td className="px-3 py-2">{u.email}</td>
                <td className="px-3 py-2"><Pill ok={u.active}>{userStatus(u)}</Pill></td><td className="px-3 py-2 text-xs">{fmtDT(u.last_login_at)}</td>
                <td className="whitespace-nowrap px-3 py-2"><button onClick={() => setPw(u)} className="rounded p-1.5 hover:bg-zinc-100" title="Şifre sıfırla" data-testid={`client-user-pw-${u.id}`}><KeyRound className="h-4 w-4" /></button>
                  <button onClick={() => toggle(u)} className="rounded p-1.5 hover:bg-zinc-100" title="Erişimi aç/kapat" data-testid={`client-user-access-${u.id}`}>{u.active ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}</button></td></tr>
            ))}
            {!users.length && <tr><td colSpan={5} className="p-4 text-center text-zinc-500">Kullanıcı yok.</td></tr>}
          </Table>
        )}
        <FormDialog open={add} title="Müşteri Kullanıcısı Ekle" fields={USER_FIELDS} initial={NO_INITIAL} onSubmit={(v) => api.post(`/ops/admin/clients/${client.id}/users`, v).then(() => toast.success("Kullanıcı eklendi"))} onClose={(ok) => { setAdd(false); if (ok) reload(); }} />
        <FormDialog open={!!pw} title="Şifre Sıfırla" fields={PW} initial={NO_INITIAL} onSubmit={(v) => api.post(`/ops/admin/users/${pw.id}/password`, v).then(() => toast.success("Şifre sıfırlandı"))} onClose={() => setPw(null)} />
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [rows, reload] = useData("/ops/admin/clients");
  const [form, setForm] = useState(null);
  const [users, setUsers] = useState(null);
  const save = (v) => (form.id ? api.put(`/ops/admin/clients/${form.id}`, v) : api.post("/ops/admin/clients", v)).then(() => toast.success("Müşteri kaydedildi"));
  const del = (c) => confirmRun(`${c.name} silinsin mi?`, async () => { await api.delete(`/ops/admin/clients/${c.id}`); reload(); }, "Müşteri silindi");
  return (
    <div>
      <Title title="Müşteriler" desc="Müşteri işletmeleri ve müşteri paneli kullanıcıları.">
        <button onClick={() => setForm({ active: true })} className="btn-red !py-2" data-testid="client-add-btn"><Plus className="h-4 w-4" /> Müşteri Ekle</button>
      </Title>
      {!rows ? <Loading /> : (
        <Table head={CLIENT_HEAD} testid="clients-table">
          {rows.map((c) => (
            <tr key={c.id} data-testid={`client-row-${c.id}`}>
              <td className="px-3 py-2 font-semibold text-[color:var(--red)]">{c.code}</td><td className="px-3 py-2 font-semibold">{c.name}</td>
              <td className="px-3 py-2">{c.contact_name || "-"}</td><td className="whitespace-nowrap px-3 py-2">{c.phone || "-"}</td><td className="px-3 py-2">{c.projects}</td>
              <td className="px-3 py-2"><Pill ok={c.active}>{c.active ? "Aktif" : "Pasif"}</Pill></td>
              <td className="whitespace-nowrap px-3 py-2">
                <button onClick={() => setUsers(c)} className="rounded p-1.5 hover:bg-zinc-100" title="Kullanıcılar" data-testid={`client-users-${c.id}`}><UserPlus className="h-4 w-4" /></button>
                <button onClick={() => setForm(c)} className="rounded p-1.5 hover:bg-zinc-100" title="Düzenle" data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => del(c)} className="rounded p-1.5 text-red-700 hover:bg-red-50" title="Sil" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={7} className="p-6 text-center text-zinc-500">Henüz müşteri eklenmedi.</td></tr>}
        </Table>
      )}
      <FormDialog open={!!form} title={form?.id ? "Müşteriyi Düzenle" : "Yeni Müşteri"} fields={FIELDS} initial={form} onSubmit={save} onClose={(ok) => { setForm(null); if (ok) reload(); }} />
      <UsersDialog client={users} onClose={() => setUsers(null)} />
    </div>
  );
}
